<script setup lang="ts">
import { computed } from "vue"
import DataState from "../components/DataState.vue"
import MetricTile from "../components/MetricTile.vue"
import PageHeader from "../components/PageHeader.vue"
import RecommendationCard from "../components/RecommendationCard.vue"
import StatusBadge from "../components/StatusBadge.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useDashboardStore } from "../stores/dashboard"
import { formatNumber } from "../utils/format"

const dashboard = useDashboardStore()
const highCount = computed(
  () => dashboard.recommendations.filter((recommendation) => recommendation.severity === "high").length
)
const affectedCount = computed(() =>
  dashboard.recommendations.reduce((total, recommendation) => total + recommendation.affectedCount, 0)
)
</script>

<template>
  <DashboardLayout>
    <div class="grid gap-5">
      <PageHeader
        title="Recommendations"
        description="Evidence-linked follow-up actions."
        details="Recommended operator actions derived from dashboard data and explicit telemetry gaps."
      >
        <template #actions>
          <StatusBadge status="high" />
          <span class="dashboard-mono text-sm text-(--dash-muted)">{{ formatNumber(affectedCount) }} affected</span>
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="dashboard.recommendations.length === 0"
        empty-centered
        empty-title="No recommendations"
        empty-description="No active follow-up actions."
      >
        <section class="grid gap-3 md:grid-cols-3">
          <MetricTile
            label="Total recommendations"
            :value="formatNumber(dashboard.recommendations.length)"
            tone="success"
            caption="Evidence-linked actions"
          />
          <MetricTile
            label="High severity"
            :value="formatNumber(highCount)"
            tone="warning"
            caption="Capacity or reliability risk"
          />
          <MetricTile
            label="Affected entities"
            :value="formatNumber(affectedCount)"
            tone="neutral"
            caption="Nodes, sessions, or events"
          />
        </section>

        <section class="grid gap-4 lg:grid-cols-2">
          <RecommendationCard
            v-for="recommendation in dashboard.recommendations"
            :key="recommendation.id"
            :recommendation="recommendation"
          />
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
